import React, { useEffect, useState } from 'react';
import './Contact.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID = 'service_7uw6nzj';

const getEmailConfig = () => ({
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '',
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '',
});

const Contact = () => {
  const [form, setForm] = useState({
    user_name: '',
    user_email: '',
    user_phone: '',
    message: '',
  });
  const [status, setStatus] = useState('idle');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      offset: 100,
      easing: 'ease-out',
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const { publicKey, templateId } = getEmailConfig();

    if (!publicKey || !templateId) {
      setSubmitError(
        'This form is not fully configured yet. Please email us directly or try again later.'
      );
      return;
    }

    setStatus('sending');
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, templateId, { ...form }, publicKey);
      setStatus('success');
      setForm({ user_name: '', user_email: '', user_phone: '', message: '' });
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('idle');
      setSubmitError('Something went wrong sending your message. Please call or email us directly.');
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content" data-aos="fade-up">
          <h1 className="gradient-text">Contact Us</h1>
          <p>Let's Discuss Your Project</p>
        </div>
      </div>

      <div className="contact-main">
        <div className="contact-card-grid">
          <div className="contact-card" data-aos="fade-up">
            <h2 className="contact-card-title">Get in Touch</h2>
            <p className="contact-card-desc">We're here to help bring your metal fabrication vision to life. Contact us today to discuss your project.</p>
            <div className="contact-info-list">
              <div className="contact-info-item">
                <FaMapMarkerAlt className="contact-info-icon" />
                <div>
                  <div className="contact-info-label">Our Location</div>
                  <div>30 Westwyn Court, Brampton, ON L6T 4T5</div>
                </div>
              </div>
              <div className="contact-info-item">
                <FaPhoneAlt className="contact-info-icon" />
                <div>
                  <div className="contact-info-label">Sales Number</div>
                  <div>
                    <a href="tel:+16478259116" className="contact-email-link">
                      (647) 825-9116
                    </a>
                  </div>
                </div>
              </div>
              <div className="contact-info-item">
                <FaPhoneAlt className="contact-info-icon" />
                <div>
                  <div className="contact-info-label">Office Number</div>
                  <div>
                    <a href="tel:+19057906870" className="contact-email-link">
                      (905) 790-6870
                    </a>
                  </div>
                </div>
              </div>
              <div className="contact-info-item">
                <FaEnvelope className="contact-info-icon" />
                <div>
                  <div className="contact-info-label">Email</div>
                  <div>
                    <a href="mailto:sales@advancedmetalshop.ca" className="contact-email-link">
                      Sales@advancedmetalshop.ca
                    </a>
                  </div>
                </div>
              </div>
              <div className="contact-info-item">
                <FaClock className="contact-info-icon" />
                <div>
                  <div className="contact-info-label">Business Hours</div>
                  <div><br/>Monday - Thurday: 7:00 AM - 4:00 PM<br/></div>
                  <div><br/>Friday: 7:00 AM - 3:00 PM</div>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-card" data-aos="fade-up" data-aos-delay="200">
            <h2 className="contact-card-title">Send Us a Message</h2>
            {status === 'success' ? (
              <div className="contact-form-success">
                <p>Thank you! Your message has been sent. We will get back to you soon.</p>
                <button
                  type="button"
                  className="submit-button contact-form-reset"
                  onClick={() => setStatus('idle')}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form className="contact-form contact-form-inline" onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label htmlFor="user_name">Name</label>
                  <input
                    id="user_name"
                    name="user_name"
                    type="text"
                    autoComplete="name"
                    value={form.user_name}
                    onChange={handleChange}
                    required
                    disabled={status === 'sending'}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="user_email">Email</label>
                  <input
                    id="user_email"
                    name="user_email"
                    type="email"
                    autoComplete="email"
                    value={form.user_email}
                    onChange={handleChange}
                    required
                    disabled={status === 'sending'}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="user_phone">Phone <span className="label-optional">(optional)</span></label>
                  <input
                    id="user_phone"
                    name="user_phone"
                    type="tel"
                    autoComplete="tel"
                    value={form.user_phone}
                    onChange={handleChange}
                    disabled={status === 'sending'}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    required
                    disabled={status === 'sending'}
                  />
                </div>
                {submitError ? (
                  <div className="contact-form-error" role="alert">
                    {submitError}
                  </div>
                ) : null}
                <button type="submit" className="submit-button" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending…' : 'Send message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
